import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { borderGradientStyle, createVar } from '@sorare/core/src/style/utils';

import { gradientLevels } from 'components/managerId/ui';
import {
  ColorLevelTreshold,
  getLevelTreshold,
} from 'components/managerId/utils';

import { LevelBar_ManagerProgression } from './__generated__/index.graphql';

const textColors: Record<ColorLevelTreshold, string> = {
  0: '#FFF',
  10: '#FFDFA4',
  20: '#FFB6B6',
  30: '#babded',
  40: '#797283',
};

const textColor = createVar();
const gradientLevel = createVar();

export const TRANSITION_DURATION = 1;

const Root = styled.div`
  min-width: 200px;
  flex: 1;
  height: var(--double-unit);
  padding: 1px;
  border-radius: var(--double-unit);
  background: var(--c-nd-100-opaque);

  --border-size: 1px;
  --border-gradient: var(${gradientLevel});
  ${borderGradientStyle}
`;
const ProgressContainer = styled.div`
  position: absolute;
  inset: 2px;
  overflow: hidden;
  border-radius: inherit;
`;
const Progress = styled.div`
  --progress-bg: var(${gradientLevel});

  border-top-left-radius: inherit;
  border-bottom-left-radius: inherit;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: var(--progress-bg);

  .animated &,
  &.grey {
    width: 0;
    transition: width ${TRANSITION_DURATION}s var(--c-cb-ease-out-md);
  }

  &.grey {
    --progress-bg: var(--c-nd-200);
  }
`;
const Levels = styled(SBHorizontal)`
  position: relative;
  height: 100%;
  padding: 0 var(--unit);
  align-items: center;
`;
const Level = styled.p`
  font-size: 18px;
  font-family:
    Druk Wide,
    sans-serif;
  text-transform: uppercase;

  color: var(${textColor});
`;

type Props = {
  managerProgression: Pick<
    LevelBar_ManagerProgression,
    'levelReached' | 'points' | 'pointsForNextLevel'
  >;
  animated?: boolean;
  animatedPoints?: number;
  className?: string;
  hideLevels?: boolean;
};

const MIN_PERCENT = 2;

export const LevelBar = ({
  managerProgression,
  animated,
  animatedPoints,
  className,
  hideLevels,
}: Props) => {
  const { levelReached, points, pointsForNextLevel } = managerProgression;
  const levelTreshold = getLevelTreshold(levelReached);

  const endOfLevel = points + pointsForNextLevel;

  return (
    <Root
      className={classNames(className, { animated })}
      style={{
        [gradientLevel]: gradientLevels[levelTreshold],

        [textColor]: textColors[levelTreshold],
      }}
    >
      <ProgressContainer>
        {animated && animatedPoints !== undefined && (
          <Progress
            className="grey"
            style={{
              width: `${Math.max(
                MIN_PERCENT,
                (animatedPoints / endOfLevel) * 100
              )}%`,
            }}
          />
        )}
        <Progress
          style={{
            width: `${Math.max(MIN_PERCENT, (points / endOfLevel) * 100)}%`,
          }}
        />
      </ProgressContainer>
      {!hideLevels && (
        <Levels>
          <Level>{levelReached}</Level>
          <Level>{levelReached + 1}</Level>
        </Levels>
      )}
    </Root>
  );
};

LevelBar.fragments = {
  managerProgression: gql`
    fragment LevelBar_ManagerProgression on ManagerProgression {
      id
      levelReached
      points
      pointsForNextLevel
    }
  ` as TypedDocumentNode<LevelBar_ManagerProgression>,
};
